import {HStack, Integer} from 'platform/foundation-tw';
import {cva} from 'platform/shared-tw';
import {match, Pattern} from 'ts-pattern';

import {isNotNil} from 'ramda';

import type {MiraType} from '../../types';
import {MiraIcon} from '../MiraIcon/MiraIcon';

const VARIANT_ICON_MAP: Record<MiraType, 'mira/damage' | 'mira/service' | 'mira/suggestions'> = {
  damage: 'mira/damage',
  service: 'mira/service',
  suggestions: 'mira/suggestions',
};

type BadgeSize = 'sm' | 'md';

const badgeVariants = cva('box-border rounded-full', {
  variants: {
    type: {
      damage: 'mira-type-damage bg-[var(--mira-bg)]',
      service: 'mira-type-service bg-[var(--mira-bg)]',
      suggestions: 'mira-type-suggestions bg-[var(--mira-bg)]',
    },
    size: {
      sm: 'py-1 pr-2.5 pl-2',
      md: 'py-2 pr-4 pl-3',
    },
  },
  defaultVariants: {size: 'md'},
});

const textVariants = cva(
  'shrink-0 font-medium whitespace-nowrap tabular-nums text-[var(--mira-text)]',
  {
    variants: {
      size: {
        sm: 'text-xs leading-none',
        md: 'text-lg leading-6',
      },
    },
    defaultVariants: {size: 'md'},
  }
);

const iconVariants = cva('flex shrink-0 items-center justify-center text-[var(--mira-stroke)]', {
  variants: {
    size: {
      sm: 'size-4',
      md: 'size-6',
    },
  },
  defaultVariants: {size: 'md'},
});

const ICON_LAYOUT_BY_SIZE = {
  sm: {iconSize: 4, iconSpacing: 1},
  md: {iconSize: 6, iconSpacing: 2},
} as const satisfies Record<BadgeSize, {iconSize: Integer; iconSpacing: Integer}>;

export interface MainBadgeProps {
  readonly type: MiraType;
  readonly count: number;
  readonly size?: BadgeSize;
  readonly label?: string;
}

/**
 * MAINBADGE
 * Displays a badge with an icon and count for different Mira types.
 *
 * @prop type - The type of badge (damage, service, suggestions)
 * @prop count - Number to display in the badge
 * @prop size - Visual size: `md` (default) for section headers, `sm` for inline rows
 * @prop label - Severity noun (e.g. "Damages"). When provided, the badge is exposed to
 *   assistive tech as `role="img"` with `aria-label="{count} {label}"` and its inner
 *   content is hidden; omit it for purely decorative count badges.
 */
export function MainBadge(props: MainBadgeProps) {
  const size = props.size ?? 'md';
  const iconName = VARIANT_ICON_MAP[props.type];
  const isLabelled = isNotNil(props.label);

  const {iconSize, iconSpacing} = ICON_LAYOUT_BY_SIZE[size];

  const accessibilityProps = match(props.label)
    .with(Pattern.string, (label) => ({
      role: 'img' as const,
      'aria-label': `${props.count} ${label}`,
    }))
    .otherwise(() => ({}));

  return (
    <div className={badgeVariants({type: props.type, size})} {...accessibilityProps}>
      <HStack spacing={iconSpacing} align="center">
        <span aria-hidden className={iconVariants({size})}>
          <MiraIcon name={iconName} size={iconSize} />
        </span>
        <span aria-hidden={isLabelled || undefined} className={textVariants({size})}>
          {props.count}
        </span>
      </HStack>
    </div>
  );
}
